import {
  ChevronRight, Loader2, Search, Plus, X,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { formatAmount } from "../movement-allocations/utils";
import type { BankMovement } from "../../core/models/admin";

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

type SettlementLinkedMovementProps = {
  linkedMovement: BankMovement | null
  isLoadingMovement: boolean
  searchQuery: string
  onSearchChange: (value: string) => void
  searchResults: BankMovement[]
  isSearching: boolean
  attachingId: string | null
  isDetaching: boolean
  onAttach: (movementId: string) => void
  onDetach: () => void
  onOpenMovement: (movementId: string) => void
};

const LinkedMovementCard = ({
  movement,
  onOpen,
  onDetach,
  isDetaching,
}: {
  movement: BankMovement
  onOpen: () => void
  onDetach: () => void
  isDetaching: boolean
}) => (
  <div
    className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 cursor-pointer hover:bg-indigo-100/60 transition-colors"
    onClick={onOpen}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[12px] font-semibold text-indigo-900 truncate">
            {movement.label || movement.id}
          </span>
          {movement.paymentMode && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-indigo-200/70 text-indigo-900 shrink-0">
              {movement.paymentMode}
            </span>
          )}
        </div>
        <div className="text-[11px] text-indigo-700 mt-0.5">
          {formatDate(movement.date)}
          {movement.debit
            ? (
              <>
                {" "}
                — Débit :
                {formatAmount(movement.debit)}
                {" "}
                €
              </>
            )
            : null}
          {movement.credit
            ? (
              <>
                {" "}
                — Crédit :
                {formatAmount(movement.credit)}
                {" "}
                €
              </>
            )
            : null}
          {movement.accountName && (
            <>
              {" "}
              —
              {movement.accountName}
            </>
          )}
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
        disabled={isDetaching}
        onClick={(e) => {
          e.stopPropagation();
          onDetach();
        }}
      >
        {isDetaching
          ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          )
          : (
            <X className="h-3.5 w-3.5" />
          )}
      </Button>
    </div>
  </div>
);

const SearchResultCard = ({
  movement,
  onAttach,
  isAttaching,
}: {
  movement: BankMovement
  onAttach: () => void
  isAttaching: boolean
}) => (
  <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2">
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-2">
        <span className="text-[12px] font-medium text-slate-800 truncate">
          {movement.label || movement.id}
        </span>
        {movement.paymentMode && (
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 shrink-0">
            {movement.paymentMode}
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500">
        {formatDate(movement.date)}
        {movement.debit
          ? (
            <>
              {" "}
              — Débit :
              {formatAmount(movement.debit)}
              {" "}
              €
            </>
          )
          : null}
        {movement.credit
          ? (
            <>
              {" "}
              — Crédit :
              {formatAmount(movement.credit)}
              {" "}
              €
            </>
          )
          : null}
      </div>
    </div>
    <Button
      type="button"
      variant="success"
      size="sm"
      className="h-7 px-2.5 text-[11px] shrink-0"
      disabled={isAttaching}
      onClick={onAttach}
    >
      {isAttaching
        ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
        )
        : (
          <Plus className="h-3.5 w-3.5 mr-1" />
        )}
      Associer
    </Button>
  </div>
);

const SettlementLinkedMovement = ({
  linkedMovement,
  isLoadingMovement,
  searchQuery,
  onSearchChange,
  searchResults,
  isSearching,
  attachingId,
  isDetaching,
  onAttach,
  onDetach,
  onOpenMovement,
}: SettlementLinkedMovementProps) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors py-1"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
        Attacher à une écriture bancaire existante
        {linkedMovement && (
          <span className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-medium bg-indigo-100 text-indigo-700 ml-1">
            1
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-1.5 space-y-2">
          {isLoadingMovement && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          )}

          {!isLoadingMovement && linkedMovement && (
            <LinkedMovementCard
              movement={linkedMovement}
              onOpen={() => onOpenMovement(linkedMovement.id)}
              onDetach={onDetach}
              isDetaching={isDetaching}
            />
          )}

          {!isLoadingMovement && !linkedMovement && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Rechercher un mouvement à associer..."
                  className="pl-9 h-9 text-[13px]"
                />
                {isSearching && (
                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                )}
              </div>

              {!isSearching && searchQuery.trim().length >= 1 && searchResults.length === 0 && (
                <div className="text-center py-3 text-[12px] text-slate-500">
                  Aucun mouvement trouvé.
                </div>
              )}

              {!isSearching && searchResults.map((movement) => (
                <SearchResultCard
                  key={movement.id}
                  movement={movement}
                  onAttach={() => onAttach(movement.id)}
                  isAttaching={attachingId === movement.id}
                />
              ))}

              {!searchQuery.trim() && (
                <div className="text-center py-3 text-[12px] text-slate-400">
                  Aucun mouvement lié à ce règlement.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SettlementLinkedMovement;
